import { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Custom hook to fetch and use CMS page content with i18n fallback
 * @param {string} pageId - The page identifier (e.g., 'home', 'aircraft', 'team')
 * @returns {Object} - { content, loading, getContent }
 */
export const usePageContent = (pageId) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contentAPI.getPageContent(pageId);
        setContent(data?.content || null);
      } catch (err) {
        console.error(`Failed to fetch content for ${pageId}:`, err);
        setContent(null);
      } finally {
        setLoading(false);
      }
    };

    if (pageId) {
      fetchContent();
    }
  }, [pageId]);

  /**
   * Get content value with automatic language detection
   * @param {string} key - Content key (e.g., 'hero_title')
   * @param {Function} t - Optional translation function for fallback
   * @param {string} i18nKey - Optional fallback i18n key
   * @param {string} lang - Optional language override
   * @returns {string} - The content value
   */
  const getContent = (key, t = null, i18nKey = null, lang = null) => {
    const currentLang = lang || language;
    
    if (content && content[key]) {
      // For Turkish language
      if (currentLang === 'tr') {
        const trValue = content[key].value_tr;
        if (trValue && trValue.trim()) {
          return trValue;
        }
        // If no Turkish CMS value, try English value
        const enValue = content[key].value;
        if (enValue && enValue.trim()) {
          return enValue;
        }
        // If still no value and i18n fallback provided
        if (t && i18nKey) {
          return t(i18nKey);
        }
      }
      // For English - return CMS value if available
      const enValue = content[key].value;
      if (enValue && enValue.trim()) {
        return enValue;
      }
    }
    // Fallback to i18n translation if provided
    if (t && i18nKey) {
      return t(i18nKey);
    }
    // Return empty string if nothing found
    return '';
  };

  /**
   * Get content value without i18n fallback
   * @param {string} key - Content key
   * @param {string} language - Current language
   * @returns {string|null} - The content value or null
   */
  const getContentRaw = (key, language) => {
    if (!content || !content[key]) return null;
    
    if (language === 'tr') {
      const trValue = content[key].value_tr;
      if (trValue && trValue.trim()) {
        return trValue;
      }
    }
    return content[key].value || null;
  };

  return { content, loading, getContent, getContentRaw };
};

/**
 * Get localized field from a data object (like team member, build log)
 * @param {Object} item - The data object
 * @param {string} field - The field name (e.g., 'title', 'role')
 * @param {string} language - Current language ('en' or 'tr')
 * @returns {string} The localized field value
 */
export const getLocalizedField = (item, field, language) => {
  if (!item) return '';
  
  if (language === 'tr') {
    const trField = item[`${field}_tr`];
    if (trField && trField.trim()) {
      return trField;
    }
  }
  
  return item[field] || '';
};

/**
 * Get localized content from CMS content object
 * @param {Object} contentItem - The content item from CMS
 * @param {string} language - Current language ('en' or 'tr')
 * @returns {string} The localized value
 */
export const getLocalizedContent = (contentItem, language) => {
  if (!contentItem) return '';
  
  if (language === 'tr') {
    const trValue = contentItem.value_tr;
    if (trValue && trValue.trim()) {
      return trValue;
    }
  }
  
  return contentItem.value || '';
};

export default usePageContent;
